import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatDateAU } from '../../../shared/format';
import { QASkeleton } from './QASkeleton';
import { QAEmpty } from './QAEmpty';

interface FileNoteFinding {
  ruleKey: string | null;
  ruleSummary: string | null;
  severity: string | null;
  status: string | null;
  recheckStatus: string | null;
  deferredTo: string | null;
}

interface FileNoteSection {
  heading?: string;
  body?: string;
  source?: string;
  confidence?: string;
}

interface FileNoteSessionRow {
  id: string;
  sourceApp: string;
  sourceReferenceId: string;
  submittedAt: string;
  clientNameRaw: string | null;
  meetingType: string | null;
  meetingDate: string | null;
  engagementStage: string | null;
  declarationName: string | null;
  declarationAt: string | null;
  skillVersion: number | null;
  finalSections: FileNoteSection[] | null;
  findingsSummary: FileNoteFinding[] | null;
  pdfUrl?: string | null;
  status: string;
  dedupeOutcome?: string | null;
}

const STAGE_LABEL: Record<string, string> = {
  first_meeting: 'First / discovery meeting',
  fact_find: 'Fact-find',
  strategy_discussion: 'Strategy discussion',
  implementation: 'Implementation',
  review: 'Review / annual review',
  ad_hoc: 'Ad-hoc',
};

const OUTCOME_LABEL: Record<string, { label: string; cls: string }> = {
  name_match: { label: 'Linked by name', cls: 'bg-forest/20 text-forest' },
  unmatched: { label: 'Unlinked — no matching client', cls: 'bg-caution/20 text-caution' },
  ambiguous: { label: 'Unlinked — multiple name matches', cls: 'bg-caution/20 text-caution' },
  no_name: { label: 'Unlinked — no client name', cls: 'bg-rust/10 text-rust' },
};

export function FileNoteSessionsTab({ clientId }: { clientId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['file-note-sessions', clientId],
    queryFn: () => api<{ data: FileNoteSessionRow[] }>(`/api/intake/file-note-sessions/by-client/${clientId}`),
  });

  if (isLoading) return <QASkeleton variant="cards" rows={2} />;
  if (isError || !data) return <div className="qa-badge qa-badge--out">Unable to load file notes</div>;

  if (data.data.length === 0) {
    return (
      <QAEmpty eyebrow="No File-Note.AI sessions yet" title="Nothing finalised yet">
        When a file note for this client is finalised in File-Note.AI, it'll land here automatically.
      </QAEmpty>
    );
  }

  return (
    <div className="space-y-4">
      {data.data.map((fn) => (
        <FileNoteSessionCard key={fn.id} fn={fn} />
      ))}
    </div>
  );
}

function FileNoteSessionCard({ fn }: { fn: FileNoteSessionRow }) {
  const outcome = fn.dedupeOutcome ? OUTCOME_LABEL[fn.dedupeOutcome] : null;
  const sections = fn.finalSections ?? [];
  const findings = fn.findingsSummary ?? [];
  const openFindings = findings.filter((f) => f.status === 'open').length;
  const deferred = findings.filter((f) => f.status === 'deferred').length;

  return (
    <div className="qa-card">
      <div className="flex items-center justify-between px-4 py-2 border-b border-hairline-paper text-xs qa-mono uppercase">
        <div className="flex items-center gap-3">
          <span className="text-ink-mute">submitted</span>
          <span className="text-ink">{formatDateAU(fn.submittedAt)}</span>
          <span className="text-ink-mute">·</span>
          <span className="text-ink-mute">via</span>
          <span className="text-ink">{fn.sourceApp}</span>
        </div>
        {outcome && <span className={`px-2 py-0.5 ${outcome.cls}`}>{outcome.label}</span>}
      </div>

      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        {fn.engagementStage && (
          <Stat label="Engagement stage" value={STAGE_LABEL[fn.engagementStage] ?? fn.engagementStage} />
        )}
        {fn.meetingType && <Stat label="Meeting type" value={fn.meetingType.replace(/_/g, ' ')} />}
        {fn.meetingDate && <Stat label="Meeting date" value={formatDateAU(fn.meetingDate)} />}
        {fn.declarationName && <Stat label="Declared by" value={fn.declarationName} />}
        {fn.declarationAt && <Stat label="Declared at" value={formatDateAU(fn.declarationAt)} />}
        {fn.skillVersion != null && <Stat label="Compliance skill" value={`v${fn.skillVersion}`} />}

        {findings.length > 0 && (
          <div className="md:col-span-2">
            <span className="qa-eyebrow text-xs block mb-1">
              Compliance findings — {findings.length} total
              {openFindings > 0 && <span className="text-caution"> · {openFindings} open</span>}
              {deferred > 0 && <span className="text-brass-deep"> · {deferred} deferred</span>}
            </span>
            <ul className="text-ink mt-1 space-y-1">
              {findings.map((f, i) => (
                <li key={i} className="flex items-baseline gap-2">
                  <span className={`text-xs qa-mono uppercase ${severityCls(f.severity)}`}>
                    {f.severity ?? '—'}
                  </span>
                  <span className="text-ink-mute">{f.ruleSummary ?? f.ruleKey ?? 'finding'}</span>
                  {f.status && <span className="text-xs text-ink-mute">({f.status.replace(/_/g, ' ')})</span>}
                </li>
              ))}
            </ul>
          </div>
        )}

        {sections.length > 0 && (
          <div className="md:col-span-2">
            <span className="qa-eyebrow text-xs block mb-1">File note sections</span>
            <ul className="text-ink mt-1 list-disc list-inside">
              {sections.map((s, i) => (
                <li key={i}>{s.heading ?? `Section ${i + 1}`}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="border-t border-hairline-paper px-4 py-2 flex items-center justify-between text-xs">
        <span className="text-ink-mute qa-mono">ref: {fn.sourceReferenceId}</span>
        {fn.pdfUrl ? (
          <a href={fn.pdfUrl} target="_blank" rel="noopener noreferrer" className="btn-ghost">
            ↓ View PDF
          </a>
        ) : (
          <span className="text-ink-mute italic">PDF not attached</span>
        )}
      </div>
    </div>
  );
}

function severityCls(sev: string | null): string {
  if (sev === 'critical') return 'text-rust';
  if (sev === 'high') return 'text-caution';
  if (sev === 'medium') return 'text-brass-deep';
  return 'text-ink-mute';
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span className="qa-eyebrow text-xs block">{label}</span>
      <span className="text-ink">{value}</span>
    </div>
  );
}
